import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Aurora } from "../components/Aurora";
import { STUDIO } from "../data/constants";
import { useAuth } from "../lib/auth.jsx";

/**
 * Public landing page — Apple.com-style single column of sections.
 *
 * Fully responsive: mobile (<640px) gets a simplified, stacked layout;
 * desktop gets the full two-column, alternating design.
 *
 * Sections, top to bottom:
 *   1. Hero          — brand mark + pitch + CTAs
 *   2. Flow          — three numbered steps
 *   3. Personalised  — skin-tone colour story
 *   4. iOS showcase  — carousel of app features
 *   5. Technician    — recruit pitch for nail techs
 *   6. Trust         — testimonial + stack credits
 *   7. CTA           — full-bleed gradient closer
 */
export function HomeScreen({ theme }) {
  const nav = useNavigate();
  const { session, profile, authedFetch } = useAuth();
  const role  = profile?.role || "customer";
  const isDark = theme === "dark";
  const [featuredDesigns, setFeaturedDesigns] = useState([]);
  const [featuredTechnicians, setFeaturedTechnicians] = useState([]);
  const [dashboard, setDashboard] = useState(null);

  const onCustomer = () => nav(session ? "/book" : "/signup");
  const onTech     = () =>
    nav(session
      ? (["technician", "admin_tech"].includes(role) ? "/tech" : "/login")
      : "/login");
  const onGallery  = () => nav("/gallery");

  const t = isDark ? darkTokens : lightTokens;

  function imageCountForProfile(profile) {
    const plan = (profile?.plan || "free").toLowerCase();
    const tier = (profile?.membership_tier || "free").toLowerCase();
    if (plan === "unlimited" || tier === "gold") return 6;
    if (plan === "studio"    || tier === "silver") return 3;
    return 1;
  }

  const count = session ? imageCountForProfile(profile) : 1;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [galleryRes, techRes] = await Promise.all([
          fetch("/api/public/gallery?limit=8"),
          fetch("/api/public/technicians"),
        ]);
        const galleryJson = galleryRes.ok ? await galleryRes.json() : {};
        const techJson = techRes.ok ? await techRes.json() : {};
        if (!cancelled) {
          setFeaturedDesigns(Array.isArray(galleryJson?.designs) ? galleryJson.designs : []);
          setFeaturedTechnicians(Array.isArray(techJson?.technicians) ? techJson.technicians : []);
        }
      } catch {
        if (!cancelled) {
          setFeaturedDesigns([]);
          setFeaturedTechnicians([]);
        }
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!session) return;
    let cancelled = false;
    (async () => {
      try {
        if (role === "admin_tech") {
          const [requests, bookings, reports] = await Promise.all([
            authedFetch("/api/admin/requests").catch(() => ({ requests: [] })),
            authedFetch("/api/admin/bookings").catch(() => ({ bookings: [] })),
            authedFetch("/api/reports/overview").catch(() => null),
          ]);
          if (!cancelled) setDashboard({ requests, bookings, reports });
        } else if (role === "technician") {
          const [assigned, inbox, earnings] = await Promise.all([
            authedFetch("/api/bookings/assigned").catch(() => ({ bookings: [] })),
            authedFetch("/api/bookings/inbox").catch(() => ({ requests: [] })),
            authedFetch("/api/tech/earnings").catch(() => null),
          ]);
          if (!cancelled) setDashboard({ assigned, inbox, earnings });
        } else {
          const [bookings, favorites] = await Promise.all([
            authedFetch("/api/bookings/mine").catch(() => ({ bookings: [] })),
            authedFetch("/api/favorites").catch(() => ({ favorites: [] })),
          ]);
          if (!cancelled) setDashboard({ bookings, favorites });
        }
      } catch {
        if (!cancelled) setDashboard(null);
      }
    })();
    return () => { cancelled = true; };
  }, [session, role]);

  if (session) {
    return (
      <RoleLanding
        t={t}
        role={role}
        profile={profile}
        dashboard={dashboard}
        designs={featuredDesigns}
        onBook={onCustomer}
        onGallery={onGallery}
        onTech={onTech}
        onAdmin={() => nav("/admin")}
        onHistory={() => nav("/history")}
      />
    );
  }

  return (
    <div
      style={{
        background: t.bg,
        color: t.text,
        position: "relative",
        overflow: "hidden",
        transition: "background .4s var(--ease-out), color .4s var(--ease-out)",
      }}
    >
      <Aurora variant={isDark ? "noir" : "ivory"} />

      <Hero          t={t} onPrimary={onCustomer} onSecondary={onGallery}
                     session={session} role={role} count={count}
                     designs={featuredDesigns} />
      <FlowSection   t={t} count={count} designs={featuredDesigns} />
      <DesignWallSection t={t} designs={featuredDesigns} onGallery={onGallery} />
      <PersonalSection t={t} />
      <IosSection    t={t} designs={featuredDesigns} technicians={featuredTechnicians} />
      <TechSection   t={t} onTech={onTech} />
      <TrustSection  t={t} />
      <ClosingCta    t={t} onPrimary={onCustomer} count={count} />
    </div>
  );
}

function landingCurrency(cents, currencyCode = "USD") {
  const code = String(currencyCode || "USD").toUpperCase();
  const symbol = {
    USD: "$", CAD: "CA$", GBP: "£", EUR: "€", NGN: "₦",
    GHS: "GH₵", ZAR: "R", KES: "KSh", AUD: "A$",
  }[code] || code;
  return `${symbol}${(Number(cents || 0) / 100).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
}

function RoleLanding({ t, role, profile, dashboard, designs, onBook, onGallery, onTech, onAdmin, onHistory }) {
  const isAdmin = role === "admin_tech";
  const isTech = role === "technician";
  const firstName = (profile?.full_name || profile?.email || "there").split(" ")[0];
  const heroImage = designs.find((d) => d?.image_url)?.image_url || "/app-icons/oge-app-icon-photoreal.png";

  const cards = isAdmin
    ? [
        ["New requests", dashboard?.requests?.requests?.filter((r) => !r.assigned_to).length ?? "—"],
        ["Today bookings", dashboard?.bookings?.bookings?.length ?? "—"],
        ["Revenue", dashboard?.reports?.revenueCents != null ? landingCurrency(dashboard.reports.revenueCents, dashboard.reports.currency) : "—"],
      ]
    : isTech
      ? [
          ["Assigned bookings", dashboard?.assigned?.bookings?.length ?? "—"],
          ["Heading your way", dashboard?.inbox?.requests?.length ?? "—"],
          ["Net earnings", dashboard?.earnings?.netCents != null ? landingCurrency(dashboard.earnings.netCents, dashboard.earnings.currency) : "—"],
        ]
      : [
          ["Appointments", dashboard?.bookings?.bookings?.length ?? "—"],
          ["Saved designs", dashboard?.favorites?.favorites?.length ?? "—"],
          ["AI ideas", designs.length || "—"],
        ];

  return (
    <div style={{
      minHeight: "100vh",
      background: "var(--bg)",
      color: "var(--text)",
      padding: "84px 16px 56px",
    }}>
      <div style={{ maxWidth: 1180, margin: "0 auto", display: "grid", gap: 22 }}>
        <section style={{
          minHeight: 430,
          borderRadius: 28,
          overflow: "hidden",
          position: "relative",
          border: "1px solid var(--border)",
          boxShadow: "var(--shadow-lg)",
          background: "var(--bg-elevated)",
        }}>
          <div aria-hidden="true" style={{
            position: "absolute",
            inset: 0,
            background: `linear-gradient(90deg, rgba(21,21,21,.88), rgba(21,21,21,.48), rgba(21,21,21,.12)), url(${heroImage}) center/cover`,
          }} />
          <div style={{ position: "relative", padding: "clamp(28px, 6vw, 64px)", maxWidth: 720 }}>
            <div style={{ color: "#d7c28a", fontWeight: 900, fontSize: 12, letterSpacing: 1.1, textTransform: "uppercase" }}>
              {isAdmin ? "Studio command" : isTech ? "Technician workspace" : "Client workspace"}
            </div>
            <h1 style={{
              fontFamily: "var(--font-display)",
              color: "#fff",
              fontSize: "clamp(42px, 7vw, 86px)",
              lineHeight: 0.96,
              margin: "14px 0 0",
              letterSpacing: 0,
            }}>
              Welcome back, {firstName}.
            </h1>
            <p style={{ color: "rgba(255,255,255,.76)", fontSize: 18, lineHeight: 1.55, maxWidth: 620, marginTop: 18 }}>
              {isAdmin
                ? "Assign requests, watch revenue, and keep every tech moving from one focused studio view."
                : isTech
                  ? "See what is booked, what is heading your way, and open finger-by-finger work mode before the client sits down."
                  : "Review your designs, rebook favorites, and turn your next nail idea into an appointment."}
            </p>
            <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginTop: 28 }}>
              <button onClick={isAdmin ? onAdmin : isTech ? onTech : onBook} style={roleCtaStyle}>
                {isAdmin ? "Open admin" : isTech ? "Open tech board" : "Book a design"}
              </button>
              <button onClick={isAdmin || isTech ? onGallery : onHistory} style={roleGhostStyle}>
                {isAdmin || isTech ? "View gallery" : "View history"}
              </button>
            </div>
          </div>
        </section>

        <section style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))", gap: 14 }}>
          {cards.map(([label, value]) => (
            <div key={label} style={{
              background: "var(--bg-elevated)",
              border: "1px solid var(--border)",
              borderRadius: 18,
              padding: 20,
              boxShadow: "var(--shadow-sm)",
            }}>
              <div style={{ color: "var(--text-muted)", fontSize: 12, fontWeight: 900, letterSpacing: 0.8, textTransform: "uppercase" }}>{label}</div>
              <div style={{ fontFamily: "var(--font-display)", fontSize: 38, fontWeight: 900, color: "var(--text)", marginTop: 6 }}>{value}</div>
            </div>
          ))}
        </section>
      </div>
    </div>
  );
}

const roleCtaStyle = {
  border: "none",
  borderRadius: 999,
  background: "#d7c28a",
  color: "#161616",
  padding: "13px 22px",
  fontWeight: 900,
  cursor: "pointer",
};

const roleGhostStyle = {
  border: "1px solid rgba(255,255,255,.34)",
  borderRadius: 999,
  background: "rgba(255,255,255,.10)",
  color: "#fff",
  padding: "13px 22px",
  fontWeight: 900,
  cursor: "pointer",
};

// ════════════════════════════════════════════════════════════════════════
// Hero
// ════════════════════════════════════════════════════════════════════════
function Hero({ t, onPrimary, onSecondary, session, role, count = 1, designs = [] }) {
  const isMobile = useIsMobile();
  const isTablet = useMediaQuery("(max-width: 900px)");
  const stacked = isMobile || isTablet;
  const heroImage = designs.find((d) => d?.image_url)?.image_url || "/app-icons/oge-app-icon-photoreal.png";

  return (
    <section
      style={{
        position: "relative", zIndex: 1,
        minHeight: "calc(100svh - 60px)",
        display: "flex",
        alignItems: "flex-end",
        padding: isMobile ? "78px 18px 154px" : (isTablet ? "118px 28px 64px" : "132px 28px 54px"),
        overflow: "hidden",
      }}
    >
      <div aria-hidden="true" style={{
        position: "absolute",
        inset: 0,
        background: `linear-gradient(180deg, rgba(13,8,16,0.18), rgba(13,8,16,0.72)), url(${heroImage}) center/cover no-repeat`,
        transform: "scale(1.02)",
      }} />
      <div aria-hidden="true" style={{
        position: "absolute",
        inset: 0,
        background: t.isDark
          ? "linear-gradient(90deg, rgba(15,9,21,0.94), rgba(15,9,21,0.42) 56%, rgba(15,9,21,0.18))"
          : "linear-gradient(90deg, rgba(253,248,243,0.94), rgba(253,248,243,0.58) 54%, rgba(253,248,243,0.18))",
      }} />

      <div style={{
        position: "relative",
        width: "100%",
        maxWidth: 1180,
        margin: "0 auto",
        display: "grid",
        gridTemplateColumns: stacked ? "minmax(0, 1fr)" : "minmax(0, 0.92fr) minmax(300px, 0.55fr)",
        gap: isMobile ? 22 : (isTablet ? 28 : 44),
        alignItems: "end",
      }}>
        <div style={{ textAlign: "left", maxWidth: 760 }}>
          <Eyebrow t={t}>Oge Eyesthetic Nail Studio</Eyebrow>
          <h1 style={{
            fontFamily: "var(--font-display)",
            fontSize: isMobile
              ? "clamp(42px, 11vw, 56px)"
              : (isTablet ? "clamp(58px, 9vw, 76px)" : "clamp(64px, 8vw, 104px)"),
            fontWeight: 800,
            letterSpacing: 0,
            lineHeight: 0.96,
            color: t.text,
            marginTop: 16,
          }}>
            Design the set before the appointment.
          </h1>
          <p style={{
            fontSize: isMobile ? 16 : 20,
            color: t.textMuted,
            maxWidth: 620,
            marginTop: isMobile ? 18 : 22,
            lineHeight: 1.55,
          }}>
            AI previews, skin-tone aware renders, booking, Wallet, widgets, and Live Activities arranged as one native-feeling studio workflow.
          </p>

          <div style={{
            display: "flex",
            flexDirection: isMobile ? "column" : "row",
            gap: 12,
            alignItems: isMobile ? "stretch" : "center",
            marginTop: isMobile ? 24 : 30,
            maxWidth: isMobile ? 340 : "none",
          }}>
            <CtaPrimary onClick={onPrimary} fullWidth={isMobile}>
              {session ? (role === "customer" ? "Book your design" : "Open dashboard") : "Get started"}
            </CtaPrimary>
            <CtaGhost t={t} onClick={onSecondary} fullWidth={isMobile}>View real work</CtaGhost>
          </div>
        </div>

        {!isMobile && (
          <div style={{
            display: "grid",
            gap: 10,
            paddingBottom: 6,
          }}>
            {[
              ["01", `${count} AI design${count === 1 ? "" : "s"}`],
              ["02", "Book and pay in flow"],
              ["03", "iPhone, iPad, and web"],
            ].map(([n, label]) => (
              <div key={n} style={{
                display: "grid",
                gridTemplateColumns: "48px 1fr",
                gap: 12,
                alignItems: "center",
                padding: "14px 16px",
                borderRadius: 18,
                background: t.isDark ? "rgba(255,255,255,0.06)" : "rgba(255,255,255,0.68)",
                border: `1px solid ${t.divider}`,
                backdropFilter: "blur(18px)",
              }}>
                <span style={{ color: t.gold, fontWeight: 800, fontSize: 12 }}>{n}</span>
                <span style={{ color: t.text, fontWeight: 700 }}>{label}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

// ════════════════════════════════════════════════════════════════════════
// "How it works" — three steps
// ════════════════════════════════════════════════════════════════════════
function FlowSection({ t, count, designs = [] }) {
  const isMobile = useIsMobile();
  const isCompact = useMediaQuery("(max-width: 1040px)");

  const steps = [
    {
      n: "01",
      title: "Pick your vibe.",
      body: "Choose an occasion or upload an inspo photo. We listen to what you want — formal, edgy, soft, statement — and use it as the brief.",
      art: <ArtVibe designs={designs} />,
    },
    {
      n: "02",
      title: `AI crafts ${count} design${count === 1 ? '' : 's'}.`,
      body: `Our AI creates ${count} unique look${count === 1 ? '' : 's'} tuned to your category, then renders ${count === 1 ? 'it' : 'each'} on a photoreal hand that matches your skin tone in seconds.`,
      art: <ArtDesigns />,
    },
    {
      n: "03",
      title: "Book your slot.",
      body: "Pick the design you love, choose an open time, pay securely. Your technician gets the brief, the supplies list, and the render — ready before you walk in.",
      art: <ArtBook />,
    },
  ];

  return (
    <SectionShell t={t}>
      <SectionHead t={t}
        eyebrow="How it works"
        title="From idea to appointment in three steps."
      />

      <div style={{
        display: "grid",
        gridTemplateColumns: isCompact ? "1fr" : "repeat(3, minmax(0, 1fr))",
        gap: isMobile ? 16 : 18,
        marginTop: isMobile ? 34 : 52,
        alignItems: "stretch",
      }}>
        {steps.map((s, i) => (
          <Reveal key={s.n}>
            <LandingCard t={t} style={{ height: "100%", padding: 0, overflow: "hidden" }}>
              <div style={{ padding: isMobile ? 18 : 20 }}>
                <div style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 12,
                  marginBottom: 14,
                }}>
                  <span style={{
                    fontSize: 12,
                    fontWeight: 800,
                    letterSpacing: 1.8,
                    textTransform: "uppercase",
                    color: t.gold,
                  }}>
                    Step {s.n}
                  </span>
                  <span style={{
                    width: 34,
                    height: 34,
                    borderRadius: 12,
                    display: "inline-grid",
                    placeItems: "center",
                    background: t.isDark ? "rgba(255,255,255,0.08)" : "rgba(13,8,16,0.05)",
                    color: t.textMuted,
                    fontSize: 13,
                    fontWeight: 800,
                  }}>
                    {i + 1}
                  </span>
                </div>
                <h3 style={{
                  fontFamily: "var(--font-display)",
                  fontSize: isMobile ? 24 : 28,
                  fontWeight: 780,
                  lineHeight: 1.08,
                  color: t.text,
                }}>
                  {s.title}
                </h3>
                <p style={{
                  fontSize: isMobile ? 14 : 15,
                  color: t.textMuted,
                  lineHeight: 1.62,
                  marginTop: 12,
                }}>
                  {s.body}
                </p>
              </div>
              <div style={{ padding: isMobile ? "0 12px 12px" : "0 14px 14px" }}>
                {s.art}
              </div>
            </LandingCard>
          </Reveal>
        ))}
      </div>
    </SectionShell>
  );
}

// ════════════════════════════════════════════════════════════════════════
// "Personalised" — colour-tone strip + skin-tone pitch
// ════════════════════════════════════════════════════════════════════════
function PersonalSection({ t }) {
  const isMobile = useIsMobile();
  const tones = [
    "#F4D7C8", "#E8B8A1", "#D29473", "#A86F47",
    "#7A4F2F", "#5A3320", "#3E2014", "#2A140A",
  ];
  return (
    <SectionShell t={t} narrow>
      <Reveal>
        <div style={{
          display: "grid",
          gridTemplateColumns: isMobile ? "1fr" : "0.95fr 1.05fr",
          gap: isMobile ? 18 : 28,
          alignItems: "stretch",
        }}>
          <LandingCard t={t} style={{ padding: isMobile ? 22 : 30 }}>
            <Eyebrow t={t}>Made for every skin tone</Eyebrow>
            <h2 style={{
              fontFamily: "var(--font-display)",
              fontSize: isMobile ? "clamp(30px, 9vw, 44px)" : "clamp(38px, 5vw, 64px)",
              fontWeight: 820,
              letterSpacing: 0,
              lineHeight: 1.02,
              color: t.text,
              marginTop: 18,
            }}>
              Every preview, on a hand that looks like yours.
            </h2>
            <p style={{
              fontSize: "clamp(15px, 2vw, 17px)",
              color: t.textMuted,
              lineHeight: 1.7,
              marginTop: 20,
            }}>
              Set your skin tone once. Every design render uses that context so a nude chrome, oxblood almond, or soft blush finish reads closer to the real appointment.
            </p>
          </LandingCard>

          <LandingCard t={t} style={{ padding: isMobile ? 16 : 22, display: "grid", alignContent: "center" }}>
            <div style={{
              display: "grid",
              gridTemplateColumns: "repeat(4, 1fr)",
              gap: 10,
            }}>
              {tones.map((c, i) => (
                <div key={i} style={{
                  aspectRatio: "1 / 1",
                  borderRadius: 18,
                  background: c,
                  boxShadow: "inset 0 0 0 1px rgba(0,0,0,0.06), 0 12px 24px rgba(0,0,0,0.08)",
                }} />
              ))}
            </div>
            <div style={{
              marginTop: 16,
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 10,
            }}>
              {["Skin-aware render", "Saved profile"].map((label) => (
                <div key={label} style={{
                  padding: "12px 14px",
                  borderRadius: 16,
                  background: t.isDark ? "rgba(255,255,255,0.055)" : "rgba(13,8,16,0.045)",
                  color: t.text,
                  fontSize: 13,
                  fontWeight: 700,
                }}>
                  {label}
                </div>
              ))}
            </div>
          </LandingCard>
        </div>
      </Reveal>
    </SectionShell>
  );
}

function DesignWallSection({ t, designs = [], onGallery }) {
  const isMobile = useIsMobile();
  const visible = designs.slice(0, 5);
  const fallback = [
    { name: "Soft chrome", category: "Minimal", image_url: "" },
    { name: "Oxblood almond", category: "Evening", image_url: "" },
    { name: "Blush french", category: "Classic", image_url: "" },
    { name: "Gold detail", category: "Editorial", image_url: "" },
    { name: "Clean nude", category: "Everyday", image_url: "" },
  ];
  const cards = visible.length ? visible : fallback;

  return (
    <SectionShell t={t}>
      <div style={{
        display: "grid",
        gridTemplateColumns: isMobile ? "1fr" : "0.7fr 1.3fr",
        gap: isMobile ? 22 : 34,
        alignItems: "center",
      }}>
        <Reveal>
          <div>
            <Eyebrow t={t}>Gallery signal</Eyebrow>
            <h2 style={{
              fontFamily: "var(--font-display)",
              fontSize: isMobile ? "clamp(30px, 9vw, 44px)" : "clamp(42px, 5.2vw, 66px)",
              fontWeight: 820,
              lineHeight: 1.02,
              color: t.text,
              marginTop: 18,
            }}>
              Real studio work leads the experience.
            </h2>
            <p style={{
              fontSize: isMobile ? 15 : 17,
              lineHeight: 1.7,
              color: t.textMuted,
              marginTop: 18,
              maxWidth: 480,
            }}>
              Browse finished looks, compare tones, and move from inspiration to a booked appointment without losing the detail that made the set feel right.
            </p>
            <div style={{ marginTop: 26 }}>
              <CtaGhost t={t} onClick={onGallery}>Open gallery</CtaGhost>
            </div>
          </div>
        </Reveal>

        <Reveal>
          <div style={{
            display: "grid",
            gridTemplateColumns: isMobile ? "1fr 1fr" : "1.2fr 0.8fr 0.8fr",
            gridAutoRows: isMobile ? 150 : 180,
            gap: 12,
          }}>
            {cards.map((d, i) => (
              <LandingCard key={d.id || d.name || i} t={t} style={{
                overflow: "hidden",
                padding: 0,
                minHeight: 0,
                gridRow: !isMobile && i === 0 ? "span 2" : undefined,
              }}>
                <div style={{
                  height: "100%",
                  minHeight: 0,
                  display: "flex",
                  alignItems: "flex-end",
                  padding: 14,
                  color: "#fff",
                  background: d.image_url
                    ? `linear-gradient(180deg, rgba(13,8,16,0.08), rgba(13,8,16,0.68)), url(${d.image_url}) center/cover no-repeat`
                    : `linear-gradient(145deg, ${i % 2 ? "#4a2034" : "#2e1528"}, #0d0810)`,
                }}>
                  <div>
                    <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: 1.4, textTransform: "uppercase", color: "rgba(255,255,255,0.7)" }}>
                      {d.category || "Studio look"}
                    </div>
                    <div style={{
                      fontFamily: "var(--font-display)",
                      fontSize: i === 0 && !isMobile ? 24 : 16,
                      fontWeight: 780,
                      lineHeight: 1.08,
                      marginTop: 5,
                      textShadow: "0 2px 12px rgba(0,0,0,0.42)",
                    }}>
                      {d.name || "Featured look"}
                    </div>
                  </div>
                </div>
              </LandingCard>
            ))}
          </div>
        </Reveal>
      </div>
    </SectionShell>
  );
}

// ════════════════════════════════════════════════════════════════════════
// iOS app showcase
// ════════════════════════════════════════════════════════════════════════
function IosSection({ t, designs = [], technicians = [], onLogin }) {
  const isMobile = useIsMobile();
  const isCompact = useMediaQuery("(max-width: 1040px)");
  const isTablet = useMediaQuery("(max-width: 820px)");

  const features = [
    { icon: "AR", title: "Surface AR preview",
      body: "Place the design on a tray, desk, or table and see how it lands in the room without posing your hand." },
    { icon: "LA", title: "Live Activities",
      body: "Your booking updates on the Lock Screen and Dynamic Island with the latest status, timing, and tech." },
    { icon: "WG", title: "Home-screen widget",
      body: "Today's bookings, your next design, and a shortcut to login live on the home screen." },
    { icon: "WA", title: "Apple Wallet pass",
      body: "Add the booking pass to Wallet and walk in with one tap instead of searching for confirmation." },
  ];

  return (
    <SectionShell t={t}>
      <div style={{
        display: "grid",
        gridTemplateColumns: isCompact ? "1fr" : "repeat(auto-fit, minmax(min(100%, 360px), 1fr))",
        gap: isMobile ? 32 : 48,
        alignItems: "start",
      }}>
        <Reveal style={{ minWidth: 0 }}>
          <div style={{ minWidth: 0 }}>
            <Eyebrow t={t}>Now on iPhone</Eyebrow>
            <h2 style={{
              fontFamily: "var(--font-display)",
              fontSize: isMobile ? "clamp(28px, 8vw, 40px)" : "clamp(32px, 5.4vw, 56px)",
              fontWeight: 900, letterSpacing: 0, lineHeight: 1.05,
              color: t.text, marginTop: 18,
            }}>
              Four screens. One polished iPhone experience.
            </h2>
            <p style={{
              fontSize: isMobile ? 15 : 17, color: t.textMuted, lineHeight: 1.7,
              marginTop: 18, maxWidth: 540,
            }}>
              The iOS app is designed like a product trailer: surface AR, a
              smart home-screen widget, Live Activities, and Apple Wallet
              support, each shown in a separate screen you can swipe through.
            </p>

            <div style={{
              display: "grid",
              gridTemplateColumns: isMobile ? "1fr" : "repeat(2, minmax(0, 1fr))",
              gap: 12,
              marginTop: isMobile ? 20 : 32,
            }}>
              {features.map((f) => (
                <LandingCard key={f.title} t={t} style={{
                  padding: isMobile ? "14px" : "16px",
                  borderRadius: 18,
                  boxShadow: "none",
                }}>
                  <SymbolTile t={t}>{f.icon}</SymbolTile>
                  <div style={{
                    fontFamily: "var(--font-display)",
                    fontSize: isMobile ? 15 : 17,
                    fontWeight: 760,
                    color: t.text,
                    marginTop: 12,
                  }}>{f.title}</div>
                  <div style={{
                    fontSize: isMobile ? 12.5 : 13.5,
                    color: t.textMuted,
                    lineHeight: 1.55,
                    marginTop: 6,
                  }}>
                    {f.body}
                  </div>
                </LandingCard>
              ))}
            </div>

            <div style={{
              marginTop: isMobile ? 20 : 36,
              display: "flex",
              gap: 12,
              flexWrap: "wrap",
              alignItems: "center",
              justifyContent: isCompact ? "center" : "flex-start",
            }}>
              <CtaPrimary onClick={onLogin}>Continue to login</CtaPrimary>
              <AppStoreBadge />
            </div>
            <div style={{
              marginTop: 12, fontSize: 11,
              color: t.textOverline, letterSpacing: 1.5,
              textTransform: "uppercase",
              textAlign: isCompact ? "center" : "left",
            }}>
              Widget, Wallet, and Live Activities are built to feel native.
            </div>
          </div>
        </Reveal>

        <Reveal style={{ minWidth: 0 }}>
          <IosShowcaseCarousel t={t} designs={designs} technicians={technicians} onLogin={onLogin} isMobile={isMobile} isCompact={isCompact} isTablet={isTablet} />
        </Reveal>
      </div>
    </SectionShell>
  );
}

/**
 * "Available on the App Store" badge — pill that mirrors Apple's marketing
 * style. Swap for an <a> tag once the App Store listing is live.
 */
function AppStoreBadge() {
  const [hover, setHover] = useState(false);
  return (
    <button
      type="button"
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onClick={() => alert("Coming soon — App Store listing in review.")}
      style={{
        display: "inline-flex", alignItems: "center", gap: 12,
        padding: "10px 18px",
        background: "#000",
        color: "#fff",
        borderRadius: 12,
        border: "1px solid rgba(255,255,255,0.12)",
        cursor: "pointer",
        boxShadow: hover ? "0 14px 32px rgba(0, 0, 0, 0.32)" : "0 6px 18px rgba(0, 0, 0, 0.22)",
        transform: hover ? "translateY(-2px)" : "translateY(0)",
        transition: "all .25s var(--ease-spring)",
        flexShrink: 0,
      }}
    >
      <svg width="22" height="26" viewBox="0 0 26 32" fill="currentColor" aria-hidden="true">
        <path d="M21.6 17.06c-.04-3.86 3.16-5.7 3.3-5.8-1.8-2.62-4.6-2.98-5.6-3.04-2.4-.24-4.66 1.4-5.86 1.4-1.22 0-3.08-1.36-5.06-1.32-2.6.04-5 1.5-6.34 3.84-2.7 4.7-.7 11.66 1.94 15.5 1.3 1.86 2.84 3.96 4.86 3.88 1.94-.08 2.68-1.26 5.04-1.26 2.36 0 3.02 1.26 5.06 1.22 2.1-.04 3.42-1.9 4.7-3.78 1.48-2.16 2.1-4.26 2.12-4.36-.04-.02-4.06-1.56-4.1-6.18zM17.74 5.7c1.06-1.32 1.78-3.12 1.58-4.94-1.54.06-3.4 1.04-4.5 2.34-.98 1.16-1.84 3.02-1.62 4.78 1.72.14 3.48-.88 4.54-2.18z"/>
      </svg>
      <div style={{ textAlign: "left", lineHeight: 1.1 }}>
        <div style={{ fontSize: 9, letterSpacing: 0.6, opacity: 0.85, textTransform: "uppercase" }}>
          Download on the
        </div>
        <div style={{ fontSize: 18, fontWeight: 700, fontFamily: "var(--font-system)", marginTop: 1 }}>
          App&nbsp;Store
        </div>
      </div>
    </button>
  );
}

function IosShowcaseCarousel({ t, designs = [], technicians = [], onLogin, isMobile, isCompact, isTablet }) {
  const slides = buildIosSlides({ t, designs, technicians, onLogin, isMobile, isCompact });
  const [index, setIndex] = useState(0);
  const trackRef = useRef(null);
  const slideRefs = useRef([]);

  const scrollToIndex = (nextIndex, behavior = "smooth") => {
    const track = trackRef.current;
    const slide = slideRefs.current[nextIndex];
    if (!track || !slide) return;
    track.scrollTo({ left: slide.offsetLeft, behavior });
    setIndex(nextIndex);
  };

  useEffect(() => {
    if (slides.length < 2) return undefined;
    const id = setInterval(() => {
      setIndex((prev) => {
        const next = (prev + 1) % slides.length;
        requestAnimationFrame(() => scrollToIndex(next));
        return next;
      });
    }, 5200);
    return () => clearInterval(id);
  }, [slides.length]);

  useEffect(() => {
    setIndex(0);
    requestAnimationFrame(() => scrollToIndex(0, "auto"));
  }, [designs, technicians]);

  return (
    <div style={{
      position: "relative",
      borderRadius: isMobile ? 20 : 30,
      border: `1px solid ${t.divider}`,
      background: t.isDark
        ? "linear-gradient(180deg, rgba(255,255,255,0.05), rgba(255,255,255,0.02))"
        : "linear-gradient(180deg, rgba(255,255,255,0.82), rgba(255,255,255,0.68))",
      boxShadow: t.isDark
        ? "0 26px 80px rgba(13, 8, 16, 0.4)"
        : "0 26px 80px rgba(13, 8, 16, 0.12)",
      padding: isMobile ? 10 : 14,
      overflow: "hidden",
    }}>
      <div
        ref={trackRef}
        className="ios-showcase-track"
        style={{
          display: "flex",
          overflowX: "auto",
          overflowY: "hidden",
          borderRadius: isMobile ? 14 : 22,
          scrollSnapType: "x mandatory",
          scrollBehavior: "smooth",
          WebkitOverflowScrolling: "touch",
          overscrollBehaviorX: "contain",
          touchAction: "pan-x pan-y",
          scrollbarWidth: "none",
          userSelect: "none",
        }}
        onScroll={(e) => {
          const width = e.currentTarget.clientWidth || 1;
          const next = Math.round(e.currentTarget.scrollLeft / width);
          if (next !== index && next >= 0 && next < slides.length) setIndex(next);
        }}
      >
        {slides.map((slide, i) => (
          <div
            key={slide.key}
            ref={(node) => { slideRefs.current[i] = node; }}
            style={{
              width: "100%",
              flex: "0 0 100%",
              scrollSnapAlign: "start",
              scrollSnapStop: "always",
            }}
          >
            {slide.render()}
          </div>
        ))}
      </div>

      {/* Controls row */}
      <div style={{
        display: "flex",
        alignItems: "center",
        justifyContent: isTablet ? "center" : "space-between",
        gap: 8,
        marginTop: isMobile ? 10 : 14,
        flexWrap: "wrap",
      }}>
        {/* Tab badges */}
        <div style={{
          display: "flex",
          gap: 6,
          flexWrap: "wrap",
          flex: isTablet ? "0 0 100%" : 1,
          minWidth: 0,
          justifyContent: isTablet ? "center" : "flex-start",
        }}>
          {slides.map((slide, i) => (
            <button
              key={slide.key}
              type="button"
              onClick={() => scrollToIndex(i)}
              style={{
                border: "none",
                background: i === index ? "var(--mauve)" : (t.isDark ? "rgba(255,255,255,0.08)" : "rgba(13,8,16,0.06)"),
                color: i === index ? "#fff" : t.textMuted,
                borderRadius: 12,
                padding: isMobile ? "6px 10px" : "8px 12px",
                fontSize: isMobile ? 10 : 11,
                letterSpacing: 1,
                textTransform: "uppercase",
                cursor: "pointer",
                flexShrink: 0,
              }}
            >
              {slide.badge}
            </button>
          ))}
        </div>
        {/* Prev/next arrows */}
        <div style={{ display: "flex", gap: 6, flexShrink: 0, justifyContent: "center" }}>
          <CarouselNavButton
            onClick={() => scrollToIndex((index - 1 + slides.length) % slides.length)}
            ariaLabel="Previous slide"
            isMobile={isMobile}
          />
          <CarouselNavButton
            onClick={() => scrollToIndex((index + 1) % slides.length)}
            ariaLabel="Next slide"
            isMobile={isMobile}
          />
        </div>
      </div>
    </div>
  );
}

function CarouselNavButton({ onClick, ariaLabel, isMobile }) {
  const size = isMobile ? 34 : 40;
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={ariaLabel}
      style={{
        width: size, height: size,
        borderRadius: 14,
        border: "1px solid rgba(255,255,255,0.14)",
        background: "rgba(13,8,16,0.58)",
        color: "#fff",
        fontSize: isMobile ? 16 : 18,
        cursor: "pointer",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        boxShadow: "0 10px 22px rgba(13,8,16,0.18)",
        flexShrink: 0,
      }}
    >
      {ariaLabel.includes("Previous") ? "‹" : "›"}
    </button>
  );
}

function buildIosSlides({ t, designs, technicians, onLogin, isMobile, isCompact }) {
  const designA = designs[0];
  const designB = designs[1] || designs[0];
  const designC = designs[2] || designs[0];
  const designD = designs[3] || designs[0];
  const techA = technicians[0];
  const techB = technicians[1] || technicians[0];
  const techC = technicians[2] || technicians[0];
  const techD = technicians[3] || technicians[0];

  const avatar = (tech) => tech?.profile?.avatar_url || tech?.avatar_url || "";
  const name = (tech, fallback) => tech?.profile?.full_name || tech?.full_name || fallback;

  return [
    {
      key: "surface",
      badge: "Surface AR",
      render: () => (
        <div style={iosSlideShell("linear-gradient(180deg, #21101f, #0d0810)", t, isMobile)}>
          <div style={iosSlideFrame()}>
            <div style={iosSlidePill()}>AR preview</div>
            <div style={iosSurfaceHero(designA?.image_url, isMobile)} />
            <div style={iosSlideFooterGrid(isMobile)}>
              <div>
                <div style={iosSlideTitle(isMobile)}>Jaw-dropping preview, grounded in reality.</div>
                <div style={iosSlideBody(isMobile)}>
                  Drop a look onto a tray, a desk, or a table and see the design in context before you book.
                </div>
              </div>
              {!isMobile && <div style={iosImageChip(designA?.image_url, "Featured design")} />}
            </div>
          </div>
        </div>
      ),
    },
    {
      key: "widget",
      badge: "Widget",
      render: () => (
        <div style={iosSlideShell("linear-gradient(180deg, #18111f, #09060b)", t, isMobile)}>
          <div style={iosSlideFrame()}>
            <div style={iosSlidePill()}>Home screen widget</div>
            {isMobile ? (
              /* Mobile: full-width single column */
              <div style={iosWidgetFrame(isMobile)}>
                <div style={iosTinyLabel()}>TODAY</div>
                <div style={iosSlideTitle(isMobile)}>8:30 PM · Fill-in with Nia</div>
                <div style={iosSlideBody(isMobile)}>
                  The widget surfaces the next booking, the salon location, and one-tap login.
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 8, marginTop: 12 }}>
                  {[techA, techB, techC, techD].map((tech, i) => (
                    <div key={i} style={{ ...iosAvatarCard(avatar(tech), name(tech, `Tech ${i + 1}`)), minHeight: 56 }} />
                  ))}
                </div>
              </div>
            ) : (
              <div style={{
                display: "grid",
                gridTemplateColumns: isCompact ? "1fr" : "1.05fr 0.95fr",
                gap: 14,
                alignItems: "stretch",
                minHeight: 0,
              }}>
                <div style={iosWidgetFrame(false)}>
                  <div style={iosTinyLabel()}>TODAY</div>
                  <div style={iosSlideTitle(false)}>8:30 PM · Fill-in with Nia</div>
                  <div style={iosSlideBody(false)}>
                    The widget surfaces the next booking, the salon location, and one-tap login.
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 10, marginTop: 14 }}>
                    {[techA, techB, techC, techD].map((tech, i) => (
                      <div key={i} style={iosAvatarCard(avatar(tech), name(tech, `Tech ${i + 1}`))} />
                    ))}
                  </div>
                </div>
                {!isCompact && <div style={iosPhoneTile(designB?.image_url, false)}>
                  <div style={iosPhoneTileOverlay()}>
                    <div style={iosTinyLabel()}>HOME SCREEN</div>
                    <div style={iosSlideTitle(false)}>Your booking, your designs, your studio.</div>
                  </div>
                </div>}
              </div>
            )}
          </div>
        </div>
      ),
    },
    {
      key: "live",
      badge: "Live",
      render: () => (
        <div style={iosSlideShell("linear-gradient(180deg, #0f1118, #09060b)", t, isMobile)}>
          <div style={iosSlideFrame()}>
            <div style={iosSlidePill()}>Live Activities</div>
            {isMobile ? (
              /* Mobile: single column stack */
              <div style={iosLockScreen(isMobile)}>
                <div style={iosTinyLabel()}>LOCK SCREEN</div>
                <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 12 }}>
                  <div style={iosAvatarRing(avatar(techA))} />
                  <div style={{ minWidth: 0 }}>
                    <div style={iosSlideTitle(isMobile)}>In progress · 32 min</div>
                    <div style={iosSlideBody(isMobile)}>Nail refresh with Adaeze</div>
                  </div>
                </div>
                <div style={iosProgress()}>
                  <div style={iosProgressFill(0.62)} />
                </div>
                <div style={iosSlideBody(isMobile)}>
                  Quick updates, countdowns, and booking changes stay visible without opening the app.
                </div>
              </div>
            ) : (
              <div style={{
                display: "grid",
                gridTemplateColumns: isCompact ? "1fr" : "0.9fr 1.1fr",
                gap: 14,
                alignItems: "stretch",
              }}>
                <div style={iosLockScreen(false)}>
                  <div style={iosTinyLabel()}>LOCK SCREEN</div>
                  <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 14 }}>
                    <div style={iosAvatarRing(avatar(techA))} />
                    <div style={{ minWidth: 0 }}>
                      <div style={iosSlideTitle(false)}>In progress · 32 min</div>
                      <div style={iosSlideBody(false)}>Nail refresh with Adaeze</div>
                    </div>
                  </div>
                  <div style={iosProgress()}>
                    <div style={iosProgressFill(0.62)} />
                  </div>
                  <div style={iosSlideBody(false)}>
                    Quick updates, countdowns, and booking changes stay visible without opening the app.
                  </div>
                </div>
                {!isCompact && <div style={iosPortraitPanel(designC?.image_url)}>
                  <div style={iosPortraitPanelCaption()}>
                    <div style={iosTinyLabel()}>REAL STUDIO MOMENT</div>
                    <div style={iosSlideTitle(false)}>Photoreal previews. Real people.</div>
                  </div>
                </div>}
              </div>
            )}
          </div>
        </div>
      ),
    },
    {
      key: "wallet",
      badge: "Wallet",
      render: () => (
        <div style={iosSlideShell("linear-gradient(180deg, #150d1b, #09060b)", t, isMobile)}>
          <div style={iosSlideFrame()}>
            <div style={iosSlidePill()}>Apple Wallet pass</div>
            {isMobile ? (
              /* Mobile: single column */
              <div style={iosWalletCard(isMobile)}>
                <div style={iosTinyLabel()}>WALLET PASS</div>
                <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 12 }}>
                  <div style={iosAvatarRing(avatar(techC))} />
                  <div style={{ minWidth: 0 }}>
                    <div style={iosSlideTitle(isMobile)}>Booked for Sat · 11:00 AM</div>
                    <div style={iosSlideBody(isMobile)}>Almond Oxblood · Studio 02</div>
                  </div>
                </div>
                <div style={{ marginTop: 14, display: "flex", gap: 12, alignItems: "center" }}>
                  <div style={{ flex: 1 }}>
                    <div style={iosSlideBody(isMobile)}>
                      The pass carries the booking, check-in, and location details in a clean Apple Wallet layout.
                    </div>
                  </div>
                  <div style={{ ...iosQrBadge(), width: 64, height: 64 }} />
                </div>
              </div>
            ) : (
              <div style={{
                display: "grid",
                gridTemplateColumns: isCompact ? "1fr" : "1.1fr 0.9fr",
                gap: 14,
                alignItems: "stretch",
              }}>
                <div style={iosWalletCard(false)}>
                  <div style={iosTinyLabel()}>WALLET PASS</div>
                  <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 14 }}>
                    <div style={iosAvatarRing(avatar(techC))} />
                    <div style={{ minWidth: 0 }}>
                      <div style={iosSlideTitle(false)}>Booked for Sat · 11:00 AM</div>
                      <div style={iosSlideBody(false)}>Almond Oxblood · Studio 02</div>
                    </div>
                  </div>
                  <div style={{ marginTop: 16, display: "grid", gridTemplateColumns: "1fr auto", gap: 12, alignItems: "center" }}>
                    <div style={iosSlideBody(false)}>
                      The pass carries the booking, check-in, and location details in a clean Apple Wallet layout.
                    </div>
                    <div style={iosQrBadge()} />
                  </div>
                </div>
                {!isCompact && <div style={iosPhoneTile(designD?.image_url, false)}>
                  <div style={iosPhoneTileOverlay()}>
                    <div style={iosTinyLabel()}>WALLET ART</div>
                    <div style={iosSlideTitle(false)}>Elegant, fast, and easy to hand to the desk.</div>
                  </div>
                </div>}
              </div>
            )}
            <div style={{ marginTop: 16, display: "flex", justifyContent: isMobile ? "center" : "flex-end" }}>
              <button
                type="button"
                onClick={onLogin}
                style={{
                  padding: isMobile ? "12px 24px" : "12px 18px",
                  borderRadius: 14,
                  border: "none",
                  cursor: "pointer",
                  background: "linear-gradient(135deg, var(--mauve), var(--mauve-deep))",
                  color: "#fff",
                  fontWeight: 700,
                  fontSize: isMobile ? 14 : 13,
                  boxShadow: "0 12px 28px rgba(196, 135, 154, 0.34)",
                  width: isMobile ? "100%" : "auto",
                }}
              >
                Login to continue
              </button>
            </div>
          </div>
        </div>
      ),
    },
  ];
}

function iosSlideShell(background, t, isMobile) {
  return {
    minHeight: isMobile ? "auto" : "clamp(430px, 52vh, 620px)",
    borderRadius: isMobile ? 16 : 24,
    background,
    border: `1px solid ${t.isDark ? "rgba(255,255,255,0.08)" : "rgba(255,255,255,0.3)"}`,
    boxShadow: "inset 0 1px 0 rgba(255,255,255,0.04)",
    padding: isMobile ? 14 : 18,
    position: "relative",
    overflow: "hidden",
    color: "#fff",
  };
}

function iosSlideFrame() {
  return {
    position: "relative",
    height: "100%",
    display: "flex",
    flexDirection: "column",
    gap: 12,
    justifyContent: "space-between",
  };
}

function iosSlidePill() {
  return {
    display: "inline-flex",
    alignSelf: "flex-start",
    padding: "5px 10px",
    borderRadius: 999,
    background: "rgba(255,255,255,0.08)",
    fontSize: 10,
    fontWeight: 800,
    letterSpacing: 1.6,
    textTransform: "uppercase",
    color: "rgba(255,255,255,0.86)",
  };
}

function iosSurfaceHero(imageUrl, isMobile) {
  return {
    minHeight: isMobile ? "clamp(160px, 44vw, 220px)" : "clamp(260px, 36vw, 380px)",
    borderRadius: isMobile ? 14 : 22,
    overflow: "hidden",
    background: imageUrl
      ? `linear-gradient(180deg, rgba(13,8,16,0.08), rgba(13,8,16,0.38)), url(${imageUrl}) center/cover no-repeat`
      : "linear-gradient(145deg, #4a2034, #1b0d18)",
    boxShadow: "0 18px 48px rgba(0,0,0,0.3)",
    border: "1px solid rgba(255,255,255,0.08)",
  };
}

function iosPhoneTile(imageUrl, isMobile) {
  return {
    minHeight: isMobile ? "clamp(160px, 40vw, 220px)" : "clamp(200px, 28vw, 300px)",
    borderRadius: isMobile ? 14 : 22,
    overflow: "hidden",
    background: imageUrl
      ? `linear-gradient(180deg, rgba(13,8,16,0.08), rgba(13,8,16,0.36)), url(${imageUrl}) center/cover no-repeat`
      : "linear-gradient(145deg, #4a2034, #1b0d18)",
    boxShadow: "0 18px 48px rgba(0,0,0,0.24)",
    border: "1px solid rgba(255,255,255,0.08)",
    position: "relative",
  };
}

function iosPhoneTileOverlay() {
  return {
    position: "absolute",
    inset: "auto 14px 14px 14px",
    padding: "12px 12px 10px",
    borderRadius: 16,
    background: "linear-gradient(180deg, rgba(13,8,16,0.14), rgba(13,8,16,0.72))",
    backdropFilter: "blur(10px)",
  };
}

function iosWidgetFrame(isMobile) {
  return {
    minHeight: isMobile ? "auto" : "clamp(200px, 30vw, 360px)",
    borderRadius: isMobile ? 16 : 24,
    padding: isMobile ? 14 : 18,
    background: "linear-gradient(180deg, rgba(255,255,255,0.12), rgba(255,255,255,0.06))",
    border: "1px solid rgba(255,255,255,0.1)",
    boxShadow: "0 18px 40px rgba(0,0,0,0.22)",
    backdropFilter: "blur(16px)",
  };
}

function iosLockScreen(isMobile) {
  return {
    minHeight: isMobile ? "auto" : "clamp(200px, 30vw, 360px)",
    borderRadius: isMobile ? 16 : 24,
    padding: isMobile ? 14 : 18,
    background: "linear-gradient(180deg, rgba(255,255,255,0.08), rgba(255,255,255,0.04))",
    border: "1px solid rgba(255,255,255,0.1)",
    boxShadow: "0 18px 40px rgba(0,0,0,0.22)",
    backdropFilter: "blur(16px)",
  };
}

function iosWalletCard(isMobile) {
  return {
    minHeight: isMobile ? "auto" : "clamp(200px, 30vw, 360px)",
    borderRadius: isMobile ? 16 : 24,
    padding: isMobile ? 14 : 18,
    background:
      "linear-gradient(135deg, rgba(232,180,184,0.14), rgba(139,58,84,0.72)), linear-gradient(180deg, rgba(255,255,255,0.08), rgba(255,255,255,0.04))",
    border: "1px solid rgba(255,255,255,0.1)",
    boxShadow: "0 18px 40px rgba(0,0,0,0.22)",
    backdropFilter: "blur(16px)",
  };
}

function iosPortraitPanel(designImage) {
  return {
    minHeight: "clamp(200px, 30vw, 360px)",
    borderRadius: 24,
    overflow: "hidden",
    position: "relative",
    background: designImage
      ? `linear-gradient(180deg, rgba(13,8,16,0.06), rgba(13,8,16,0.42)), url(${designImage}) center/cover no-repeat`
      : "linear-gradient(145deg, #311824, #0f0a11)",
    border: "1px solid rgba(255,255,255,0.08)",
    boxShadow: "0 18px 40px rgba(0,0,0,0.22)",
  };
}

function iosPortraitPanelCaption() {
  return {
    position: "absolute",
    inset: "auto 14px 14px 14px",
    padding: "12px 12px 10px",
    borderRadius: 16,
    background: "linear-gradient(180deg, rgba(13,8,16,0.14), rgba(13,8,16,0.72))",
    backdropFilter: "blur(10px)",
  };
}

function iosAvatarCard(avatarUrl) {
  return {
    minHeight: 56,
    borderRadius: 14,
    padding: 10,
    background: avatarUrl
      ? `linear-gradient(180deg, rgba(13,8,16,0.04), rgba(13,8,16,0.6)), url(${avatarUrl}) center/cover no-repeat`
      : "linear-gradient(135deg, #4a2034, #1b0d18)",
    border: "1px solid rgba(255,255,255,0.08)",
    display: "flex",
    alignItems: "flex-end",
    boxShadow: "0 10px 20px rgba(0,0,0,0.18)",
  };
}

function iosAvatarRing(avatarUrl) {
  return {
    width: 48,
    height: 48,
    flexShrink: 0,
    borderRadius: "50%",
    background: avatarUrl
      ? `linear-gradient(180deg, rgba(13,8,16,0.04), rgba(13,8,16,0.4)), url(${avatarUrl}) center/cover no-repeat`
      : "linear-gradient(135deg, #4a2034, #1b0d18)",
    border: "1px solid rgba(255,255,255,0.12)",
    boxShadow: "0 8px 20px rgba(0,0,0,0.18)",
  };
}

function iosProgress() {
  return {
    marginTop: 14,
    height: 8,
    borderRadius: 999,
    background: "rgba(255,255,255,0.08)",
    overflow: "hidden",
  };
}

function iosProgressFill(percentage) {
  return {
    height: "100%",
    width: `${Math.round(percentage * 100)}%`,
    borderRadius: 999,
    background: "linear-gradient(90deg, var(--mauve), var(--gold-light))",
  };
}

function iosQrBadge() {
  return {
    width: 80,
    height: 80,
    flexShrink: 0,
    borderRadius: 16,
    background:
      "linear-gradient(90deg, rgba(255,255,255,0.9) 50%, rgba(13,8,16,0.95) 50%), linear-gradient(0deg, rgba(13,8,16,0.95) 50%, rgba(255,255,255,0.9) 50%)",
    backgroundSize: "12px 12px",
    boxShadow: "inset 0 0 0 6px rgba(255,255,255,0.12)",
  };
}

function iosTinyLabel() {
  return {
    fontSize: 9,
    letterSpacing: 1.6,
    textTransform: "uppercase",
    color: "rgba(255,255,255,0.65)",
    fontWeight: 800,
  };
}

function iosSlideTitle(isMobile) {
  return {
    fontFamily: "var(--font-display)",
    fontSize: isMobile ? "clamp(16px, 4.5vw, 22px)" : "clamp(18px, 2vw, 24px)",
    fontWeight: 800,
    lineHeight: 1.1,
    letterSpacing: 0,
    color: "#fff",
    marginTop: 6,
    overflowWrap: "normal",
    wordBreak: "normal",
    hyphens: "none",
  };
}

function iosSlideBody(isMobile) {
  return {
    marginTop: 8,
    fontSize: isMobile ? 12.5 : 13.5,
    lineHeight: 1.6,
    color: "rgba(255,255,255,0.76)",
    maxWidth: 360,
  };
}

function iosImageChip(imageUrl, label) {
  return {
    width: 100,
    minHeight: 80,
    borderRadius: 14,
    overflow: "hidden",
    flexShrink: 0,
    background: imageUrl
      ? `linear-gradient(180deg, rgba(13,8,16,0.06), rgba(13,8,16,0.4)), url(${imageUrl}) center/cover no-repeat`
      : "linear-gradient(145deg, #4a2034, #1b0d18)",
    border: "1px solid rgba(255,255,255,0.1)",
    boxShadow: "0 14px 32px rgba(0,0,0,0.22)",
  };
}

function iosSlideFooterGrid(isMobile) {
  return {
    display: "grid",
    gridTemplateColumns: isMobile ? "1fr" : "minmax(0, 1fr) auto",
    gap: 10,
    alignItems: "end",
  };
}

// ════════════════════════════════════════════════════════════════════════
// Technician pitch
// ════════════════════════════════════════════════════════════════════════
function TechSection({ t, onTech }) {
  const isMobile = useIsMobile();
  const isTablet = useMediaQuery("(max-width: 900px)");

  const features = [
    { icon: "CA", title: "Calendar & templates",   body: "Define your weekly schedule once; materialise four weeks of slots in one click." },
    { icon: "CH", title: "Customer chat",           body: "Every booking gets its own thread. Discuss before, during, and after the appointment." },
    { icon: "PY", title: "Stripe payouts",          body: "Connected accounts, automatic transfers on each booking, refunds + reversals handled for you." },
    { icon: "RP", title: "Earnings + reports",      body: "Gross, tips, no-shows, and net — all the numbers you need at a glance." },
  ];

  return (
    <SectionShell t={t} dark={!t.isDark} accent>
      <Reveal>
        <div style={{ textAlign: "center", maxWidth: 720, margin: "0 auto" }}>
          <Eyebrow t={t} alt>For technicians</Eyebrow>
          <h2 style={{
            fontFamily: "var(--font-display)",
            fontSize: isMobile ? "clamp(28px, 8vw, 44px)" : "clamp(32px, 6vw, 56px)",
            fontWeight: 900, letterSpacing: 0, lineHeight: 1.05,
            color: t.invertText, marginTop: 18,
          }}>
            Run your studio from one place.
          </h2>
          <p style={{
            fontSize: isMobile ? 15 : 17, color: t.invertMuted, lineHeight: 1.7,
            marginTop: 18, maxWidth: 560, margin: "18px auto 0",
          }}>
            Bookings, calendar, payments, and customer chat in a single
            dashboard built for nail technicians — with everything an admin
            needs to oversee a multi-tech studio.
          </p>
        </div>
      </Reveal>

      <Reveal>
        <div style={{
          marginTop: isMobile ? 36 : 64,
          display: "grid",
          gridTemplateColumns: isMobile ? "1fr" : (isTablet ? "repeat(2, minmax(0, 1fr))" : "repeat(4, minmax(0, 1fr))"),
          gap: 12,
          maxWidth: 1000, marginInline: "auto",
        }}>
          {features.map((f) => (
            <div key={f.title} style={{
              background: "rgba(255, 255, 255, 0.055)",
              border: "1px solid rgba(255, 255, 255, 0.10)",
              borderRadius: 18,
              padding: isMobile ? "18px" : "20px",
              minHeight: isMobile ? "auto" : (isTablet ? 210 : 230),
              display: "flex",
              flexDirection: "column",
            }}>
              <SymbolTile dark>{f.icon}</SymbolTile>
              <div style={{
                fontFamily: "var(--font-display)",
                fontSize: isMobile ? 18 : 20,
                fontWeight: 760,
                color: t.invertText,
                marginTop: 18,
              }}>
                {f.title}
              </div>
              <div style={{
                fontSize: isMobile ? 13.5 : 14,
                color: t.invertMuted,
                lineHeight: 1.6,
                marginTop: 8,
              }}>
                {f.body}
              </div>
            </div>
          ))}
        </div>
      </Reveal>

      <Reveal>
        <div style={{ textAlign: "center", marginTop: isMobile ? 32 : 56 }}>
          <CtaPrimary onClick={onTech}>Open technician dashboard →</CtaPrimary>
        </div>
      </Reveal>
    </SectionShell>
  );
}

// ════════════════════════════════════════════════════════════════════════
// Trust / stack credits
// ════════════════════════════════════════════════════════════════════════
function TrustSection({ t }) {
  const isMobile = useIsMobile();

  const stack = [
    { name: "Design AI",  by: "Creative engine" },
    { name: "Render AI",  by: "Photoreal previews" },
    { name: "Stripe",     by: "Payments" },
    { name: "Secure Cloud", by: "Data platform" },
  ];
  return (
    <SectionShell t={t}>
      <Reveal>
        <LandingCard t={t} style={{
          padding: isMobile ? 24 : 42,
          display: "grid",
          gridTemplateColumns: isMobile ? "1fr" : "0.7fr 1.3fr",
          gap: isMobile ? 18 : 30,
          alignItems: "center",
        }}>
          <div>
            <Eyebrow t={t}>Early access</Eyebrow>
            <div style={{
              marginTop: 18,
              display: "grid",
              gap: 10,
            }}>
              {["Fast booking", "Real previews", "Studio-ready"].map((label) => (
                <div key={label} style={{
                  padding: "12px 14px",
                  borderRadius: 16,
                  background: t.isDark ? "rgba(255,255,255,0.055)" : "rgba(13,8,16,0.045)",
                  color: t.text,
                  fontWeight: 700,
                  fontSize: 13,
                }}>
                  {label}
                </div>
              ))}
            </div>
          </div>
          <blockquote style={{
            fontFamily: "var(--font-display)",
            fontStyle: "italic",
            fontSize: isMobile ? "clamp(22px, 6vw, 30px)" : "clamp(30px, 4vw, 46px)",
            fontWeight: 680,
            lineHeight: 1.18,
            margin: 0,
            color: t.text,
            letterSpacing: 0,
          }}>
            "It feels like a stylist who knows exactly what I want, before I do."
            <footer style={{
              marginTop: 18,
              fontStyle: "normal",
              fontFamily: "var(--font-system)",
              fontSize: 12,
              fontWeight: 700,
              color: t.textMuted,
              letterSpacing: 1.6,
              textTransform: "uppercase",
            }}>
              Early access, Lagos
            </footer>
          </blockquote>
        </LandingCard>
      </Reveal>

      <Reveal>
        <div style={{
          marginTop: isMobile ? 48 : 80,
          display: "grid",
          gridTemplateColumns: isMobile ? "repeat(2, minmax(0, 1fr))" : "repeat(4, minmax(0, 1fr))",
          gap: isMobile ? 12 : 24,
          maxWidth: 880, marginInline: "auto",
          textAlign: "center",
        }}>
          {stack.map((s) => (
            <LandingCard key={s.name} t={t} style={{
              padding: isMobile ? "14px 8px" : "18px 12px",
              boxShadow: "none",
              borderRadius: 18,
            }}>
              <div style={{
                fontFamily: "var(--font-display)",
                fontSize: isMobile ? 14 : 18, fontWeight: 700, color: t.text,
              }}>{s.name}</div>
              <div style={{
                fontSize: isMobile ? 9 : 11, color: t.textOverline,
                letterSpacing: isMobile ? 1 : 2, textTransform: "uppercase", marginTop: 4,
              }}>{s.by}</div>
            </LandingCard>
          ))}
        </div>
      </Reveal>
    </SectionShell>
  );
}

// ════════════════════════════════════════════════════════════════════════
// Closing CTA
// ════════════════════════════════════════════════════════════════════════
function ClosingCta({ t, onPrimary, count }) {
  const isMobile = useIsMobile();
  return (
    <section style={{
      position: "relative",
      zIndex: 1,
      padding: isMobile
        ? "40px 16px 64px"
        : "clamp(64px, 12vw, 96px) clamp(16px, 4vw, 24px) clamp(80px, 16vw, 128px)",
    }}>
      <Reveal>
        <div style={{
          maxWidth: 1100, margin: "0 auto",
          background: t.isDark
            ? "linear-gradient(135deg, rgba(255,255,255,0.08), rgba(196,135,154,0.12))"
            : "linear-gradient(135deg, rgba(255,255,255,0.9), rgba(245,230,232,0.9))",
          border: `1px solid ${t.divider}`,
          borderRadius: isMobile ? 24 : 32,
          padding: isMobile
            ? "48px 24px"
            : "clamp(42px, 8vw, 86px) clamp(26px, 6vw, 74px)",
          display: "grid",
          gridTemplateColumns: isMobile ? "1fr" : "1fr auto",
          gap: isMobile ? 28 : 40,
          alignItems: "center",
          color: t.text,
          boxShadow: t.isDark ? "0 28px 70px rgba(0,0,0,0.24)" : "0 28px 70px rgba(13,8,16,0.1)",
        }}>
          <div>
            <Eyebrow t={t}>Start the flow</Eyebrow>
            <h2 style={{
              fontFamily: "var(--font-display)",
              fontSize: isMobile ? "clamp(32px, 9vw, 46px)" : "clamp(42px, 6vw, 72px)",
              fontWeight: 840,
              letterSpacing: 0,
              lineHeight: 1,
              marginTop: 16,
            }}>
              Ready when you are.
            </h2>
            <p style={{
              fontSize: isMobile ? 15 : 18,
              color: t.textMuted,
              lineHeight: 1.6,
              maxWidth: 520,
              marginTop: 16,
            }}>
              {count} design{count === 1 ? '' : 's'}, one appointment, and a studio brief ready before you walk in.
            </p>
          </div>
          <div style={{ display: "grid", gap: 12, justifyItems: isMobile ? "stretch" : "end" }}>
            <button
              onClick={onPrimary}
              style={{
                background: "linear-gradient(135deg, var(--mauve), var(--mauve-deep))",
                color: "#fff",
                fontFamily: "var(--font-system)",
                fontWeight: 700,
                fontSize: 15,
                padding: "16px 28px",
                borderRadius: 14,
                border: "none", cursor: "pointer",
                boxShadow: "0 12px 30px rgba(196, 135, 154, 0.32)",
                transition: "transform .25s var(--ease-spring)",
                width: isMobile ? "100%" : "auto",
              }}
              onMouseEnter={(e) => (e.currentTarget.style.transform = "translateY(-2px)")}
              onMouseLeave={(e) => (e.currentTarget.style.transform = "")}
            >
              Get started
            </button>
            <span style={{ fontSize: 12, color: t.textMuted }}>Book, pay, and save your design.</span>
          </div>
        </div>
      </Reveal>
    </section>
  );
}

// ════════════════════════════════════════════════════════════════════════
// Helpers — shells, reveal, micro-components
// ════════════════════════════════════════════════════════════════════════

function SectionShell({ t, narrow = false, dark = false, accent = false, children }) {
  return (
    <section style={{
      position: "relative", zIndex: 1,
      padding: "clamp(48px, 10vw, 140px) clamp(16px, 4vw, 24px)",
      background: dark
        ? (accent ? `linear-gradient(180deg, var(--noir), #1a0e1c)` : "var(--noir)")
        : "transparent",
      color: dark ? "var(--ivory)" : t.text,
    }}>
      <div style={{ maxWidth: narrow ? 880 : 1100, margin: "0 auto" }}>
        {children}
      </div>
    </section>
  );
}

function SectionHead({ t, eyebrow, title }) {
  return (
    <Reveal>
      <div style={{ textAlign: "center", maxWidth: 720, margin: "0 auto" }}>
        <Eyebrow t={t}>{eyebrow}</Eyebrow>
        <h2 style={{
          fontFamily: "var(--font-display)",
          fontSize: "clamp(28px, 6vw, 60px)",
          fontWeight: 900, letterSpacing: 0, lineHeight: 1.05,
          color: t.text, marginTop: 18,
        }}>
          {title}
        </h2>
      </div>
    </Reveal>
  );
}

function Eyebrow({ t, alt = false, children }) {
  return (
    <div style={{
      display: "inline-flex", alignItems: "center", gap: 10,
      fontFamily: "var(--font-system)",
      fontSize: 11, fontWeight: 700,
      color: alt ? "var(--gold-light)" : t.gold,
      letterSpacing: 3, textTransform: "uppercase",
    }}>
      <span style={{
        width: 22, height: 1,
        background: `linear-gradient(90deg, transparent, ${alt ? "var(--gold-light)" : t.gold})`,
      }} />
      {children}
      <span style={{
        width: 22, height: 1,
        background: `linear-gradient(90deg, ${alt ? "var(--gold-light)" : t.gold}, transparent)`,
      }} />
    </div>
  );
}

function LandingCard({ t, children, style = {} }) {
  return (
    <div style={{
      background: t.isDark ? "rgba(255,255,255,0.045)" : "rgba(255,255,255,0.76)",
      border: `1px solid ${t.divider}`,
      borderRadius: 22,
      boxShadow: t.isDark ? "0 18px 44px rgba(0,0,0,0.22)" : "0 18px 44px rgba(13,8,16,0.07)",
      backdropFilter: "blur(18px)",
      WebkitBackdropFilter: "blur(18px)",
      ...style,
    }}>
      {children}
    </div>
  );
}

function SymbolTile({ children, t, dark = false }) {
  return (
    <span style={{
      width: 42,
      height: 42,
      borderRadius: 14,
      display: "inline-grid",
      placeItems: "center",
      background: dark ? "rgba(255,255,255,0.08)" : (t?.isDark ? "rgba(255,255,255,0.08)" : "rgba(13,8,16,0.05)"),
      border: `1px solid ${dark ? "rgba(255,255,255,0.1)" : (t?.divider || "rgba(196,135,154,0.14)")}`,
      color: dark ? "#fff" : (t?.text || "var(--text)"),
      fontSize: 18,
      fontWeight: 800,
      flexShrink: 0,
    }}>
      {children}
    </span>
  );
}

function CtaPrimary({ onClick, children, fullWidth = false }) {
  const [hover, setHover] = useState(false);
  return (
    <button
      type="button"
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        background: "linear-gradient(135deg, var(--mauve), var(--mauve-deep))",
        color: "#fff",
        fontFamily: "var(--font-system)",
        fontWeight: 700, fontSize: 15,
        padding: "16px 30px",
        borderRadius: 14,
        border: "none", cursor: "pointer",
        width: fullWidth ? "100%" : "auto",
        boxShadow: hover
          ? "0 16px 34px rgba(196, 135, 154, 0.42)"
          : "0 8px 24px rgba(196, 135, 154, 0.24)",
        transform: hover ? "translateY(-2px)" : "translateY(0)",
        transition: "all .3s var(--ease-spring)",
      }}
    >
      {children}
    </button>
  );
}

function CtaGhost({ t, onClick, children, fullWidth = false }) {
  const [hover, setHover] = useState(false);
  return (
    <button
      type="button"
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        background: hover ? t.ghostHoverBg : "transparent",
        color: t.text,
        fontFamily: "var(--font-system)",
        fontWeight: 600, fontSize: 15,
        padding: "16px 24px",
        borderRadius: 14,
        border: `1.5px solid ${t.ghostBorder}`,
        cursor: "pointer",
        width: fullWidth ? "100%" : "auto",
        transition: "background .25s var(--ease-out)",
      }}
    >
      {children}
    </button>
  );
}

/** Wraps children in a div that fades-up the first time it scrolls into view. */
function Reveal({ children, style }) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!ref.current) return;
    if (typeof IntersectionObserver === "undefined") { setVisible(true); return; }
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setVisible(true); obs.disconnect(); } },
      { rootMargin: "0px 0px -8% 0px", threshold: 0.04 },
    );
    obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? "translateY(0)" : "translateY(24px)",
        transition: "opacity .8s var(--ease-out), transform .8s var(--ease-out)",
        willChange: "opacity, transform",
        ...style,
      }}
    >
      {children}
    </div>
  );
}

/** Shared hook — true when viewport is ≤639px. */
function useIsMobile() {
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== "undefined" && window.innerWidth < 640
  );
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 639px)");
    const handler = (e) => setIsMobile(e.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);
  return isMobile;
}

function useMediaQuery(query) {
  const [matches, setMatches] = useState(
    () => typeof window !== "undefined" && window.matchMedia(query).matches
  );
  useEffect(() => {
    const mq = window.matchMedia(query);
    const handler = (e) => setMatches(e.matches);
    setMatches(mq.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, [query]);
  return matches;
}

// ── Stylised "art" tiles for the flow steps ──────────────────────────────────
function ArtVibe({ designs = [] }) {
  const isMobile = useIsMobile();
  const isCompact = useMediaQuery("(max-width: 1180px)");
  const isNarrow = useMediaQuery("(max-width: 820px)");

  const fallback = [
    { emoji: "👰", label: "Bridal" },
    { emoji: "🌃", label: "Night out" },
    { emoji: "🌿", label: "Soft" },
    { emoji: "💄", label: "Editorial" },
  ];
  const cards = (designs.length ? designs : fallback).slice(0, 4);
  const featured = cards[0];

  if (isMobile || isNarrow) {
    /* Mobile: simple 2x2 grid of vibe cards, no nested layout */
    return (
      <div style={{
        borderRadius: 18,
        background: "linear-gradient(135deg, #1c0d20, #2e1528)",
        padding: 12,
        display: "grid",
        gridTemplateColumns: isMobile ? "repeat(auto-fit, minmax(min(100%, 132px), 1fr))" : "minmax(0, 1fr)",
        gap: 10,
        maxWidth: "100%",
        overflow: "hidden",
        boxShadow: "0 18px 48px rgba(13, 8, 16, 0.3)",
      }}>
        {(isMobile ? cards : cards.slice(0, 1)).map((c, i) => (
          <div key={c.id || c.name || i} style={{
            borderRadius: 14,
            overflow: "hidden",
            aspectRatio: isMobile ? "4 / 3" : "16 / 11",
            background: c?.image_url
              ? `linear-gradient(180deg, rgba(13,8,16,0.04), rgba(13,8,16,0.58)), url(${c.image_url}) center/cover no-repeat`
              : "linear-gradient(135deg, #4a2a38, #24111d)",
            border: "1px solid rgba(232, 180, 184, 0.18)",
            boxShadow: "0 8px 20px rgba(13, 8, 16, 0.18)",
            display: "flex", alignItems: "flex-end",
            padding: 10, color: "#fff",
          }}>
            <div>
              <div style={{ fontSize: 9, letterSpacing: 1.1, textTransform: "uppercase", color: "rgba(255,255,255,0.7)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                {c.category || "Studio look"}
              </div>
              <div style={{ fontFamily: "var(--font-display)", fontSize: 13, fontWeight: 700, lineHeight: 1.1, marginTop: 4, textShadow: "0 2px 8px rgba(0,0,0,0.4)" }}>
                {c.name || fallback[i]?.label || "Look"}
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div style={{
      minHeight: isMobile ? 280 : 340,
      borderRadius: "clamp(18px, 4vw, 28px)",
      background: "linear-gradient(135deg, #1c0d20, #2e1528)",
      padding: "clamp(14px, 4vw, 28px)",
      display: "grid",
      gridTemplateColumns: isCompact ? "1fr" : "minmax(260px, 1.1fr) minmax(320px, 0.9fr)",
      gap: "clamp(10px, 2vw, 16px)",
      maxWidth: "100%",
      overflow: "hidden",
      boxShadow: "0 30px 80px rgba(13, 8, 16, 0.35)",
    }}>
      <div style={{
        minHeight: isCompact ? 190 : 0,
        borderRadius: "clamp(14px, 3vw, 22px)",
        overflow: "hidden",
        position: "relative",
        background: featured?.image_url
          ? `linear-gradient(180deg, rgba(13,8,16,0.02), rgba(13,8,16,0.35)), url(${featured.image_url}) center/cover no-repeat`
          : "linear-gradient(135deg, #3a1d2d, #1c0d20)",
        border: "1px solid rgba(232, 180, 184, 0.18)",
        boxShadow: "0 18px 40px rgba(13, 8, 16, 0.18)",
      }}>
        <div style={{
          position: "absolute", left: "16px", top: "16px",
          padding: "5px 10px", borderRadius: 12,
          background: "rgba(13, 8, 16, 0.46)", color: "#fff",
          fontSize: 10, letterSpacing: 1.4, textTransform: "uppercase", fontWeight: 800,
        }}>
          Studio gallery
        </div>
        <div style={{ position: "absolute", left: "16px", right: "16px", bottom: "16px", color: "#fff" }}>
          <div style={{
            fontFamily: "var(--font-display)",
            fontSize: isCompact ? 20 : "clamp(20px, 2.6vw, 28px)",
            fontWeight: 800,
            lineHeight: 1.05,
            textShadow: "0 2px 14px rgba(0,0,0,0.45)",
          }}>
            Real studio texture.
          </div>
        </div>
      </div>

      <div style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(138px, 1fr))",
        gap: "clamp(8px, 2vw, 14px)",
      }}>
        {cards.map((c, i) => (
          <div key={c.id || c.name || i} style={{
            borderRadius: "clamp(12px, 3vw, 18px)",
            overflow: "hidden",
            minHeight: 132,
            background: c?.image_url
              ? `linear-gradient(180deg, rgba(13,8,16,0.04), rgba(13,8,16,0.58)), url(${c.image_url}) center/cover no-repeat`
              : "linear-gradient(135deg, #4a2a38, #24111d)",
            border: "1px solid rgba(232, 180, 184, 0.18)",
            boxShadow: "0 14px 32px rgba(13, 8, 16, 0.18)",
            display: "flex", alignItems: "flex-end",
            padding: 14, color: "#fff",
          }}>
            <div>
              <div style={{ fontSize: 10, letterSpacing: 1.1, textTransform: "uppercase", color: "rgba(255,255,255,0.72)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: "100%" }}>
                {c.category || "Studio look"}
              </div>
              <div style={{
                fontFamily: "var(--font-display)", fontSize: 16, fontWeight: 700,
                lineHeight: 1.1, marginTop: 6, textShadow: "0 2px 10px rgba(0,0,0,0.4)",
                overflowWrap: "normal", wordBreak: "normal",
              }}>
                {c.name || fallback[i]?.label || "Look"}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function ArtDesigns() {
  const isMobile = useIsMobile();
  const palette = [
    ["#E8B4B8", "#C4879A", "#8B3A54"],
    ["#FFE4B5", "#E0A95F", "#7C4D1F"],
    ["#F5E6E8", "#D9A1AC", "#6B2A40"],
    ["#FBE4D8", "#C97B6F", "#3F1A1A"],
    ["#EFD5C3", "#A66E50", "#42261A"],
    ["#F4D2D9", "#9D5570", "#321024"],
  ];
  return (
    <div style={{
      minHeight: isMobile ? 300 : 340,
      borderRadius: "clamp(18px, 4vw, 28px)",
      background: "linear-gradient(135deg, #fdf8f3, #f5e6e8)",
      padding: "clamp(12px, 3vw, 20px)",
      display: "grid",
      gridTemplateRows: "auto 1fr",
      gap: "clamp(10px, 2vw, 14px)",
      boxShadow: "0 30px 80px rgba(196, 135, 154, 0.18)",
    }}>
      <div style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 10,
        color: "#2a151f",
      }}>
        <div>
          <div style={{ fontSize: 10, letterSpacing: 1.6, textTransform: "uppercase", fontWeight: 800, color: "#8B3A54" }}>
            AI design board
          </div>
          <div style={{ fontFamily: "var(--font-display)", fontSize: isMobile ? 18 : 22, fontWeight: 800, lineHeight: 1.05, marginTop: 4 }}>
            Compare finishes.
          </div>
        </div>
        <div style={{
          width: 38,
          height: 38,
          borderRadius: 14,
          display: "grid",
          placeItems: "center",
          background: "#fff",
          color: "#8B3A54",
          fontWeight: 900,
          boxShadow: "0 8px 18px rgba(13,8,16,0.08)",
        }}>
          {palette.length}
        </div>
      </div>

      <div style={{
        display: "grid",
        gridTemplateColumns: isMobile ? "repeat(2, minmax(0, 1fr))" : "repeat(3, minmax(0, 1fr))",
        gap: "clamp(8px, 2vw, 12px)",
      }}>
        {palette.map((row, i) => (
          <div key={i} style={{
            borderRadius: "clamp(12px, 2vw, 16px)",
            background: "#fff",
            padding: "clamp(7px, 1.5vw, 10px)",
            boxShadow: "0 8px 20px rgba(13, 8, 16, 0.08)",
            display: "grid",
            gap: 7,
            minHeight: isMobile ? 86 : 112,
          }}>
            <div style={{
              borderRadius: "clamp(8px, 1.5vw, 12px)",
              minHeight: 48,
              background: `linear-gradient(160deg, ${row[0]}, ${row[1]}, ${row[2]})`,
            }} />
            <div style={{ display: "flex", gap: 4 }}>
              {row.map((c) => (
                <div key={c} style={{ flex: 1, height: 5, borderRadius: 4, background: c }} />
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function ArtBook() {
  const isMobile = useIsMobile();
  return (
    <div style={{
      minHeight: isMobile ? 320 : 340,
      borderRadius: "clamp(18px, 4vw, 28px)",
      background: "linear-gradient(135deg, #2e1528, #0d0810)",
      padding: isMobile ? 18 : 22,
      display: "flex", flexDirection: "column", justifyContent: "space-between",
      color: "#fff",
      boxShadow: "0 30px 80px rgba(13, 8, 16, 0.35)",
    }}>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "flex-start" }}>
        <div style={{
          display: "inline-block",
          background: "rgba(201, 168, 76, 0.18)",
          color: "var(--gold-light)",
          fontSize: "clamp(9px, 2vw, 11px)", fontWeight: 700, letterSpacing: "clamp(1px, 0.4vw, 2px)",
          padding: "5px 12px", borderRadius: 12,
          textTransform: "uppercase",
        }}>
          Confirmed
        </div>
        <div style={{
          width: 38,
          height: 38,
          borderRadius: 14,
          display: "grid",
          placeItems: "center",
          background: "rgba(255,255,255,0.08)",
          border: "1px solid rgba(255,255,255,0.10)",
          color: "rgba(255,255,255,0.8)",
          fontWeight: 900,
        }}>
          ✓
        </div>
      </div>
      <div style={{
        display: "grid",
        gap: 10,
        marginTop: isMobile ? 14 : 20,
      }}>
        <div style={{
          padding: "12px 14px",
          borderRadius: 16,
          background: "rgba(255,255,255,0.07)",
          border: "1px solid rgba(255,255,255,0.09)",
        }}>
          <div style={{
            fontFamily: "var(--font-display)",
            fontSize: "clamp(22px, 3vw, 32px)", fontWeight: 800, lineHeight: 1.05,
          }}>
            Sat, 11:00 AM
          </div>
          <div style={{ fontSize: "clamp(11px, 2.2vw, 13px)", color: "rgba(255,255,255,0.6)", marginTop: "clamp(4px, 1vw, 6px)" }}>
            with Adaeze · Almond Oxblood · 75 min
          </div>
        </div>
        <div style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: 8,
        }}>
          {["Design saved", "Brief sent"].map((item) => (
            <div key={item} style={{
              padding: "10px 12px",
              borderRadius: 14,
              background: "rgba(255,255,255,0.055)",
              color: "rgba(255,255,255,0.78)",
              fontSize: isMobile ? 12 : 13,
              fontWeight: 700,
            }}>
              {item}
            </div>
          ))}
        </div>
      </div>
      <div style={{
        display: "flex", justifyContent: "space-between", alignItems: "center",
        paddingTop: 12, borderTop: "1px solid rgba(255, 255, 255, 0.10)",
      }}>
        <div style={{ fontSize: "clamp(10px, 2vw, 12px)", color: "rgba(255,255,255,0.55)" }}>
          Total
        </div>
        <div style={{ fontFamily: "var(--font-display)", fontSize: "clamp(16px, 3.8vw, 22px)", fontWeight: 700 }}>
          $58.00
        </div>
      </div>
    </div>
  );
}

// ── Theme tokens ──────────────────────────────────────────────────────────────
const sharedDivider = { divider: "rgba(196, 135, 154, 0.14)" };

const lightTokens = {
  isDark: false,
  bg: "var(--ivory)",
  text: "var(--noir)",
  textMuted: "var(--text-muted)",
  textOverline: "var(--mid-gray)",
  gold: "var(--gold)",
  haloShadow:
    "0 0 0 12px rgba(196, 135, 154, 0.10), 0 0 0 24px rgba(196, 135, 154, 0.05), 0 12px 36px rgba(196, 135, 154, 0.28)",
  ghostBorder: "rgba(13, 8, 16, 0.18)",
  ghostHoverBg: "rgba(13, 8, 16, 0.04)",
  invertText: "#fff",
  invertMuted: "rgba(255, 255, 255, 0.7)",
  ...sharedDivider,
};

const darkTokens = {
  isDark: true,
  bg: "var(--noir)",
  text: "#fff",
  textMuted: "rgba(255, 255, 255, 0.65)",
  textOverline: "rgba(255, 255, 255, 0.35)",
  gold: "var(--gold-light)",
  haloShadow:
    "0 0 0 12px rgba(196, 135, 154, 0.08), 0 0 0 24px rgba(196, 135, 154, 0.04), 0 12px 36px rgba(196, 135, 154, 0.35)",
  ghostBorder: "rgba(255, 255, 255, 0.18)",
  ghostHoverBg: "rgba(255, 255, 255, 0.06)",
  invertText: "#fff",
  invertMuted: "rgba(255, 255, 255, 0.7)",
  ...sharedDivider,
};
